/**
 * Resource Management Screen (Manager)
 * 
 * Managers can create, edit, and manage all resources.
 * Beautiful grid layout with filtering and search.
 * 
 * @module ResourceManagementScreen
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Plus,
  Search,
  Filter,
  RefreshCw,
  Grid as GridIcon,
  List,
  Sparkles,
  Edit,
  Trash2,
  CheckSquare,
  XCircle
} from 'lucide-react';
import { ResourceService } from '../../services/ResourceService';
import { useAuth } from '../../contexts/AuthContext';
import { ResourceCard } from '../../components/shared/ResourceCard';
import { ResourceForm } from '../../components/manager/ResourceForm';
import { Resource, CreateResourceData, ResourceFilters, ResourceCategory, ResourceType, ResourceStatistics } from '../../@types/resource';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';

/**
 * Styled Components
 */
const Header = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${Spacing.xl};
`;

const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  min-width: 300px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.lg};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
    color: white;
    box-shadow: ${Shadows.sm};
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${Shadows.md};
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.medium};
    
    &:hover {
      background: ${ThemeColors.background.default};
      border-color: ${ThemeColors.primary.main};
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FilterChips = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ active?: boolean }>`
  padding: ${Spacing.xs} ${Spacing.md};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.medium};
  background: ${props => props.active ? ThemeColors.primary.light : 'white'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.primary};
  border-radius: 20px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
    color: ${ThemeColors.primary.main};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.lg};
  margin-bottom: ${Spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${Spacing.lg};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.sm};
  border: 1px solid ${ThemeColors.border.light};
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ResourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl} ${Spacing.lg};
  background: white;
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.sm};
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto ${Spacing.lg};
  background: ${ThemeColors.background.default};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.text.secondary};

  svg {
    width: 40px;
    height: 40px;
  }
`;

const SuccessMessage = styled.div`
  padding: ${Spacing.md};
  background: ${ThemeColors.success.light};
  color: ${ThemeColors.success.dark};
  border-radius: ${BorderRadius.lg};
  margin-bottom: ${Spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/**
 * ResourceManagementScreen Component
 */
export const ResourceManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const [resources, setResources] = useState<Resource[]>([]);
  const [statistics, setStatistics] = useState<ResourceStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ResourceCategory | 'all'>('all');
  const [selectedType, setSelectedType] = useState<ResourceType | 'all'>('all');
  const [showForm, setShowForm] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | undefined>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(new Set());

  /**
   * Fetch resources
   */
  const fetchResources = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filters: ResourceFilters = {
        // Managers should see all resources regardless of status
      };

      if (selectedCategory !== 'all') {
        filters.category = selectedCategory;
      }

      if (selectedType !== 'all') {
        filters.type = selectedType;
      }

      if (searchTerm) {
        filters.searchTerm = searchTerm;
      }

      const { resources: fetchedResources } = await ResourceService.getResources(
        filters,
        { field: 'createdAt', direction: 'desc' }
      );

      setResources(fetchedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, selectedType, searchTerm]);

  /**
   * Fetch statistics
   */
  const fetchStatistics = useCallback(async () => {
    try {
      const stats = await ResourceService.getResourceStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  /**
   * Load data on mount and filter changes
   */
  useEffect(() => {
    fetchResources();
    fetchStatistics();
  }, [fetchResources, fetchStatistics]);

  /**
   * Clear success message after 5 seconds
   */
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (!selectionMode) {
      return;
    }

    setSelectedResourceIds((prev) => {
      const availableIds = new Set(resources.map((resource) => resource.id));
      let changed = false;
      const next = new Set<string>();

      prev.forEach((id) => {
        if (availableIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [resources, selectionMode]);

  /**
   * Handle create resource
   */
  const handleCreateResource = useCallback(async (data: CreateResourceData, files: File[]) => {
    if (!user?.id) return;

    try {
      setIsFormSubmitting(true);
      const result = await ResourceService.createResource(
        data,
        user.id,
        user.username || user.email || 'Unknown',
        files
      );

      if (result.success) {
        setSuccessMessage(t('resources.createSuccess', 'Resource created successfully!'));
        setShowForm(false);
        setEditingResource(undefined);
        fetchResources();
        fetchStatistics();
      } else {
        alert(`Failed to create resource: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error creating resource:', error);
      alert('Failed to create resource: ' + (error.message || 'Unknown error'));
    } finally {
      setIsFormSubmitting(false);
    }
  }, [user, fetchResources, fetchStatistics, t]);

  /**
   * Handle update resource
   */
  const handleUpdateResource = useCallback(async (data: CreateResourceData, files: File[]) => {
    if (!user?.id || !editingResource) return;

    try {
      setIsFormSubmitting(true);
      const result = await ResourceService.updateResource(
        editingResource.id,
        data,
        user.id,
        user.username || user.email || 'Unknown',
        files
      );

      if (result.success) {
        setSuccessMessage(t('resources.updateSuccess', 'Resource updated successfully!'));
        setShowForm(false);
        setEditingResource(undefined);
        fetchResources();
        fetchStatistics();
      } else {
        alert(`Failed to update resource: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error updating resource:', error);
      alert('Failed to update resource: ' + (error.message || 'Unknown error'));
    } finally {
      setIsFormSubmitting(false);
    }
  }, [user, editingResource, fetchResources, fetchStatistics, t]);

  /**
   * Handle delete resource
   */
  const handleDeleteResource = useCallback(async (resource: Resource) => {
    if (!user?.id) return;

    if (window.confirm(`Are you sure you want to delete "${resource.title}"? This action cannot be undone.`)) {
      try {
        const result = await ResourceService.deleteResource(resource.id, user.id);
        if (result.success) {
          setSuccessMessage(t('resources.deleteSuccess', 'Resource deleted successfully!'));
          fetchResources();
          fetchStatistics();
        } else {
          alert(`Failed to delete resource: ${result.error}`);
        }
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert('Failed to delete resource');
      }
    }
  }, [user, fetchResources, fetchStatistics, t]);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode(prev => {
      if (prev) {
        setSelectedResourceIds(new Set());
      }
      return !prev;
    });
  }, []);

  const handleSelectToggle = useCallback((resource: Resource) => {
    setSelectedResourceIds(prev => {
      const next = new Set(prev);
      if (next.has(resource.id)) {
        next.delete(resource.id);
      } else {
        next.add(resource.id);
      }
      return next;
    });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    if (!user?.id || selectedResourceIds.size === 0) {
      return;
    }

    const confirmMessage = t(
      'resources.confirmDeleteSelected',
      'Delete selected resources? This action cannot be undone.',
      { count: selectedResourceIds.size }
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const ids = Array.from(selectedResourceIds);
      await Promise.all(
        ids.map(async id => {
          const result = await ResourceService.deleteResource(id, user.id);
          if (!result.success) {
            throw new Error(result.error || 'Failed to delete resource');
          }
        })
      );

      setSuccessMessage(t('resources.deleteSelectedSuccess', 'Selected resources deleted successfully!'));
      setSelectedResourceIds(new Set());
      setSelectionMode(false);
      fetchResources();
      fetchStatistics();
    } catch (error: any) {
      console.error('Error deleting selected resources:', error);
      alert(error?.message || 'Failed to delete selected resources');
    }
  }, [user, selectedResourceIds, fetchResources, fetchStatistics, t]);

  const handleEditResource = useCallback((resource: Resource) => {
    setEditingResource(resource);
    setShowForm(true);
  }, []);

  /**
   * Handle resource click
   */
  const handleResourceClick = useCallback((resource: Resource) => {
    navigate(`/manager/resources/${resource.id}`);
  }, [navigate]);

  return (
    <PageContainer isMenuCollapsed={false}>
      <MainContent>
        <Header>
          <Title>{t('resources.title', 'Resource Library')}</Title>
        </Header>

        {/* Statistics */}
        {statistics && (
          <StatsGrid>
            <StatCard>
              <StatValue>{statistics.totalResources}</StatValue>
              <StatLabel>{t('resources.stats.totalResources', 'Total Resources')}</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{statistics.publishedResources}</StatValue>
              <StatLabel>{t('resources.stats.published', 'Published')}</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{statistics.totalViews}</StatValue>
              <StatLabel>{t('resources.stats.totalViews', 'Total Views')}</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{statistics.totalDownloads}</StatValue>
              <StatLabel>{t('resources.stats.totalDownloads', 'Total Downloads')}</StatLabel>
            </StatCard>
          </StatsGrid>
        )}

        {/* Success Message */}
        {successMessage && (
          <SuccessMessage>
            <span>{successMessage}</span>
            <Button variant="secondary" onClick={() => setSuccessMessage(null)}>
              {t('common.close', 'Dismiss')}
            </Button>
          </SuccessMessage>
        )}

        {/* Toolbar */}
        <Toolbar>
          <ToolbarSection>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder={t('resources.searchPlaceholder', 'Search resources...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>

            <FilterChips>
              <FilterChip
                active={selectedCategory === 'all'}
                onClick={() => setSelectedCategory('all')}
              >
                {t('resources.allCategories', 'All Categories')}
              </FilterChip>
              <FilterChip
                active={selectedCategory === 'training'}
                onClick={() => setSelectedCategory('training')}
              >
                🎓 {t('resources.categories.training', 'Training')}
              </FilterChip>
              <FilterChip
                active={selectedCategory === 'product-info'}
                onClick={() => setSelectedCategory('product-info')}
              >
                📦 {t('resources.categories.product-info', 'Products')}
              </FilterChip>
              <FilterChip
                active={selectedCategory === 'sales-materials'}
                onClick={() => setSelectedCategory('sales-materials')}
              >
                💼 {t('resources.categories.sales-materials', 'Sales')}
              </FilterChip>
            </FilterChips>
          </ToolbarSection>

          <ToolbarSection>
            <Button variant="secondary" onClick={fetchResources}>
              <RefreshCw />
              {t('common.refresh', 'Refresh')}
            </Button>
          <Button variant="secondary" onClick={toggleSelectionMode}>
            {selectionMode ? <XCircle /> : <CheckSquare />}
            {selectionMode
              ? t('resources.cancelSelection', 'Cancel Selection')
              : t('resources.selectResources', 'Select Resources')}
          </Button>
          {selectionMode && (
            <Button
              variant="primary"
              onClick={handleDeleteSelected}
              disabled={selectedResourceIds.size === 0}
            >
              <Trash2 />
              {t('resources.deleteSelected', 'Delete Selected')}
            </Button>
          )}
            <Button variant="primary" onClick={() => {
              console.log('Create Resource button clicked');
              setShowForm(true);
              console.log('showForm state set to true');
            }}>
              <Plus />
              {t('resources.createResource', 'Create Resource')}
            </Button>
          </ToolbarSection>
        </Toolbar>

        {/* Resources Grid */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: Spacing.xxl, color: ThemeColors.text.secondary }}>
            {t('resources.loading', 'Loading resources...')}
          </div>
        ) : resources.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Sparkles />
            </EmptyIcon>
            <h2>{t('resources.noResourcesYet', 'No Resources Yet')}</h2>
            <p>{t('resources.createFirstResource', 'Create your first resource to share with your team')}</p>
            <Button variant="primary" onClick={() => setShowForm(true)} style={{ marginTop: Spacing.lg }}>
              <Plus />
              {t('resources.createResource', 'Create Resource')}
            </Button>
          </EmptyState>
        ) : (
          <ResourceGrid>
            {resources.map(resource => (
              <ResourceCard
                key={resource.id}
                resource={resource}
                onClick={handleResourceClick}
                showActions
                onEdit={handleEditResource}
                onDelete={handleDeleteResource}
                selectable={selectionMode}
                selected={selectedResourceIds.has(resource.id)}
                onSelectToggle={handleSelectToggle}
              />
            ))}
          </ResourceGrid>
        )}

        {/* Resource Form Modal */}
        {showForm && (
          <>
            {console.log('Rendering ResourceForm modal, showForm:', showForm)}
            <ResourceForm
              resource={editingResource}
              mode={editingResource ? 'edit' : 'create'}
              isLoading={isFormSubmitting}
              onSubmit={editingResource ? handleUpdateResource : handleCreateResource}
              onCancel={() => {
                console.log('ResourceForm onCancel called');
                setShowForm(false);
                setEditingResource(undefined);
                setIsFormSubmitting(false);
              }}
            />
          </>
        )}
      </MainContent>
    </PageContainer>
  );
};

